import { useState, useCallback, useRef } from "react";
import { useAuthenticatedFetchWithWorkspace } from "../Keycloak";

interface GetConfigPowerBeoneReportParams {
    labelReport?: string;
    forcedUserId?: string;
    language?: string;
    formatLocale?: string;
}

export interface ConfigPowerBIReport {
    labelReport: string;
    embedUrl: string;
    reportId?: string;
    accessToken?: string;
    type?: string;

    [key: string]: string | number | boolean | undefined | ConfigPowerBIReport[];
}

const useGetConfigPowerBeoneReport = (shouldFetch: boolean = true) => {
    const [data, setData] = useState<ConfigPowerBIReport | null>(null);
    const [dataWorkspaceId, setDataWorkspaceId] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const requestIdRef = useRef(0);
    const { authFetch, isReady, workspaceId } = useAuthenticatedFetchWithWorkspace();

    const fetchPowerBeoneConfigReport = useCallback(async (params: GetConfigPowerBeoneReportParams = {}): Promise<ConfigPowerBIReport | null> => {
        if (!shouldFetch || !isReady || !workspaceId) return null;

        const requestId = requestIdRef.current + 1;
        requestIdRef.current = requestId;
        const requestWorkspaceId = workspaceId;

        setLoading(true);
        setError("");
        setData(null);
        setDataWorkspaceId(null);

        try {
            const { labelReport = '', forcedUserId = '', language = '', formatLocale = '' } = params;

            const queryParams = new URLSearchParams({
                labelReport: labelReport.toString(),
                forcedUserId: forcedUserId.toString(),
                language: language.toString(),
                formatLocale: formatLocale.toString()
            });

            const result: ConfigPowerBIReport = await authFetch(`/getConfigPowerBiForReport?${queryParams.toString()}`, {
                method: "GET",
            });

            if (requestIdRef.current === requestId) {
                setData(result);
                setDataWorkspaceId(requestWorkspaceId);
            }
            return result;
        } catch (err: unknown) {
            if (requestIdRef.current === requestId) {
                if (err instanceof Error) {
                    setError(err.message);
                } else {
                    setError("An unknown error occurred");
                }
            }
            return null;
        } finally {
            if (requestIdRef.current === requestId) {
                setLoading(false);
            }
        }
    }, [authFetch, isReady, shouldFetch, workspaceId]);

    const currentWorkspaceData = dataWorkspaceId === workspaceId ? data : null;

    return {
        fetchPowerBeoneConfigReport,
        data: currentWorkspaceData,
        dataWorkspaceId,
        loading,
        error
    };
};

export default useGetConfigPowerBeoneReport;
